import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  MapIcon,
  ListBulletIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { Button, Input, Spinner } from '@theraply/ui';
import {
  useTherapistSearch,
  useUserLocation,
  TherapistSearchFilters,
} from '../../hooks/useTherapistSearch';
import { TherapistCard } from '../../components/search/TherapistCard';
import { SearchFilters } from '../../components/search/SearchFilters';
import { MapView } from '../../components/search/MapView';

type ViewMode = 'list' | 'map';

/**
 * Main search page for finding therapists.
 *
 * Features:
 * - Search bar with location autocomplete
 * - Filter sidebar with multiple filter types
 * - List and map view toggle
 * - Pagination for list view
 * - Responsive design
 * - URL state synchronization
 *
 * Performance:
 * - Debounced search queries
 * - Query caching via React Query
 * - Lazy loaded images
 * - Virtual scrolling for large lists (TODO)
 */
export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Location search state
  const [locationQuery, setLocationQuery] = useState(searchParams.get('location') || '');

  // Get user location
  const {
    location: userLocation,
    isLoading: locationLoading,
    error: locationError,
    getCurrentLocation,
  } = useUserLocation();

  // Initialize filters from URL params
  const getInitialFilters = (): TherapistSearchFilters => {
    const filters: TherapistSearchFilters = {};

    const location = searchParams.get('location');
    if (location) {
      filters.location = location;
    }

    const radius = searchParams.get('radius');
    if (radius) {
      filters.radius = parseFloat(radius);
    }

    const specialty = searchParams.get('specialty');
    if (specialty) {
      filters.specialty = specialty.split(',');
    }

    const sessionType = searchParams.get('sessionType');
    if (sessionType) {
      filters.sessionType = sessionType as any;
    }

    const minRate = searchParams.get('minRate');
    if (minRate) {
      filters.minRate = parseFloat(minRate);
    }

    const maxRate = searchParams.get('maxRate');
    if (maxRate) {
      filters.maxRate = parseFloat(maxRate);
    }

    const page = searchParams.get('page');
    if (page) {
      filters.page = parseInt(page);
    }

    return filters;
  };

  // Search hook
  const {
    therapists,
    pagination,
    filters,
    isLoading,
    isError,
    error,
    updateFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
  } = useTherapistSearch(getInitialFilters());

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.location) {
      params.set('location', filters.location);
    }
    if (filters.radius) {
      params.set('radius', filters.radius.toString());
    }
    if (filters.specialty && filters.specialty.length > 0) {
      params.set('specialty', filters.specialty.join(','));
    }
    if (filters.sessionType) {
      params.set('sessionType', filters.sessionType);
    }
    if (filters.minRate !== undefined) {
      params.set('minRate', filters.minRate.toString());
    }
    if (filters.maxRate !== undefined) {
      params.set('maxRate', filters.maxRate.toString());
    }
    if (filters.page && filters.page > 1) {
      params.set('page', filters.page.toString());
    }

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Use user location when available
  useEffect(() => {
    if (userLocation && !filters.location && !filters.latitude) {
      updateFilters({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
      });
    }
  }, [userLocation, filters.location, filters.latitude, updateFilters]);

  // Handle location search
  const handleLocationSearch = useCallback(() => {
    if (locationQuery.trim()) {
      updateFilters({ location: locationQuery.trim() });
    }
  }, [locationQuery, updateFilters]);

  // Handle use current location
  const handleUseCurrentLocation = useCallback(() => {
    getCurrentLocation();
  }, [getCurrentLocation]);

  // Handle book now
  const handleBookNow = useCallback(
    (therapistId: number) => {
      navigate(`/therapists/${therapistId}/book`);
    },
    [navigate]
  );

  // Parse center coordinates for map
  const mapCenter = filters.latitude && filters.longitude
    ? { lat: filters.latitude, lng: filters.longitude }
    : undefined;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Find Your Therapist</h1>

          {/* Location Search */}
          <div className="flex gap-4 items-center">
            <div className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPinIcon className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Enter city, state, or zip code"
                  value={locationQuery}
                  onChange={(e) => setLocationQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLocationSearch()}
                  className="pl-10"
                />
              </div>
              <Button variant="primary" onClick={handleLocationSearch}>
                <MagnifyingGlassIcon className="h-5 w-5" />
                Search
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={handleUseCurrentLocation}
              disabled={locationLoading}
            >
              {locationLoading ? <Spinner size="sm" /> : 'Use Current Location'}
            </Button>
          </div>

          {locationError && (
            <p className="mt-2 text-sm text-red-600">{locationError}</p>
          )}

          {/* View Toggle */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ListBulletIcon className="h-5 w-5" />
              List View
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'map'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <MapIcon className="h-5 w-5" />
              Map View
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <div className="w-80 flex-shrink-0">
            <SearchFilters
              filters={filters}
              onFiltersChange={updateFilters}
              onClearFilters={clearFilters}
            />
          </div>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="lg" />
                <span className="ml-3 text-gray-600">Searching...</span>
              </div>
            ) : isError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-800 font-medium">Search Error</p>
                <p className="text-red-600 text-sm mt-1">
                  {error?.message || 'An error occurred while searching'}
                </p>
              </div>
            ) : viewMode === 'list' ? (
              <>
                {/* Results Header */}
                {pagination && (
                  <div className="mb-4 text-sm text-gray-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} -{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} therapists
                  </div>
                )}

                {/* Results List */}
                {therapists.length > 0 ? (
                  <div className="space-y-4">
                    {therapists.map((therapist) => (
                      <TherapistCard
                        key={therapist.id}
                        therapist={therapist}
                        onBookNow={handleBookNow}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <p className="text-gray-600 text-lg">No therapists found</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Try adjusting your filters or search location
                    </p>
                    <Button
                      variant="outline"
                      onClick={clearFilters}
                      className="mt-4"
                    >
                      Clear Filters
                    </Button>
                  </div>
                )}

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={prevPage}
                      disabled={!hasPrevPage}
                    >
                      Previous
                    </Button>

                    <div className="flex gap-2">
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter((page) => {
                          // Show first, last, current, and nearby pages
                          return (
                            page === 1 ||
                            page === pagination.totalPages ||
                            Math.abs(page - pagination.page) <= 1
                          );
                        })
                        .map((page, index, array) => {
                          // Add ellipsis
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;

                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && (
                                <span className="px-3 py-2 text-gray-500">...</span>
                              )}
                              <button
                                onClick={() => goToPage(page)}
                                className={`px-4 py-2 rounded-md transition-colors ${
                                  page === pagination.page
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    <Button
                      variant="outline"
                      onClick={nextPage}
                      disabled={!hasNextPage}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              /* Map View */
              <div className="h-[calc(100vh-300px)] min-h-[500px]">
                <MapView
                  therapists={therapists}
                  center={mapCenter}
                  onTherapistClick={(therapist) => {
                    console.log('Therapist clicked:', therapist);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
